import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Loader2, PenLine } from 'lucide-react';
import { booksApi } from '@/lib/api';
import LazyBookCover from './LazyBookCover';

export default function BookAutocomplete({
  onSelect,
  onManualAdd,
  onSearch,
  placeholder = 'Search books...',
  className = '',
  inputClassName = '',
  autoFocus = false,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    // Cancel any in-flight search
    if (abortRef.current) abortRef.current = false;
    const token = {};
    abortRef.current = token;

    setLoading(true);
    try {
      // Run all sources in parallel
      const [local, google, ol] = await Promise.all([
        booksApi.searchLocal(q).catch(() => []),
        booksApi.search(q).catch(() => []),
        booksApi.searchOpenLibrary(q).catch(() => []),
      ]);

      if (token !== abortRef.current) return; // stale

      // Merge — local first, then Google, then OL (deduped by title+author)
      const seen = new Set();
      const combined = [];
      for (const book of [...local, ...google, ...ol]) {
        const key = `${book.title.toLowerCase().slice(0, 25)}|${book.author.toLowerCase().slice(0, 15)}`;
        if (!seen.has(key)) {
          seen.add(key);
          combined.push(book);
        }
      }
      combined.splice(8);

      setResults(combined);
      setOpen(combined.length > 0);
      setActiveIndex(-1);
    } finally {
      if (token === abortRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, search]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (book) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    onSelect(book);
  };

  const handleKeyDown = (e) => {
    if (!open && e.key !== 'Enter') return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && results[activeIndex]) {
        e.preventDefault();
        handleSelect(results[activeIndex]);
      } else if (onSearch && query.trim()) {
        setOpen(false);
        onSearch(query.trim());
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        {loading ? (
          <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-terra animate-spin pointer-events-none" />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted pointer-events-none" />
        )}
        <input
          ref={inputRef}
          type="text"
          value={query}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => {
            setQuery(e.target.value);
            if (e.target.value.length >= 2) setOpen(true);
          }}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`w-full pl-9 pr-3 h-10 bg-cream-dark border border-cream-dark rounded-lg focus:outline-none focus:border-terra text-ink placeholder:text-ink-muted text-sm transition-colors ${inputClassName}`}
        />
      </div>

      {open && (
        <div className="absolute top-full mt-1.5 left-0 right-0 min-w-[300px] bg-cream border border-cream-dark rounded-xl shadow-2xl z-[100] overflow-hidden">
          <ul role="listbox">
            {results.map((book, i) => (
              <li key={book.google_books_id} role="option" aria-selected={i === activeIndex}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(book); }}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                    i === activeIndex ? 'bg-terra/5' : 'hover:bg-cream-dark/60'
                  }`}
                >
                  {/* Thumbnail */}
                  <div className="w-8 h-11 flex-shrink-0 rounded overflow-hidden bg-cream-dark border border-cream-dark/50">
                    <LazyBookCover src={book.cover_url} alt="" iconClassName="w-3 h-3" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink truncate font-heading leading-tight">
                      {book.title}
                    </p>
                    <p className="text-xs text-ink-muted truncate mt-0.5">{book.author}</p>
                    {book.published_date && (
                      <p className="text-xs text-ink-muted/60 mt-0.5">{book.published_date.slice(0, 4)}</p>
                    )}
                  </div>
                  {book.source === 'openlibrary' && (
                    <span className="text-xs text-ink-muted/50 shrink-0 font-mono">OL</span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          {/* Manual add option */}
          {onManualAdd && (
            <div className="border-t border-cream-dark">
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  onManualAdd(query);
                }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-cream-dark/60 transition-colors"
              >
                <PenLine className="w-3.5 h-3.5 text-terra flex-shrink-0" />
                <span className="text-sm text-terra">
                  {query.trim() ? `Add "${query.trim()}" manually` : 'Add a book manually'}
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
